/**
 * @file DemoCashCheck.js
 * @description Demo check cashing page component integrating hardware scanner API proxying and real-time OCR extraction.
 */

import "../css/pages/CashCheck.css";
import "../css/pages/DocumentCamera.css";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import MasterDropdown from "../components/MasterDropdown";
import DateInput from "../components/DateInput";
import {
  getCustomers,
  getCompanies,
  getBanks,
  scanChequeImage,
  scannerStatus as checkScannerStatus,
  scannerScan as doEpsonScan,
} from "../api/services";
import toast from "react-hot-toast";
import { getTodayChicago } from "../utils/formatDate";

// ─────────────────────────────────────────────────────────────────────────────
// ⚙️  Scanner IP — not used for Epson Connect (kept for display only)
// ─────────────────────────────────────────────────────────────────────────────
const SCANNER_IP = "Epson Connect Cloud";

/**
 * Component displaying Epson hardware scanner connectivity status bar.
 * @component
 * @param {Object} props
 * @param {string} props.status - Status state string ('ok'|'checking'|'error'|'scanning'|'unknown').
 */
function ScannerStatus({ status }) {
  const cfg = {
    unknown:  { bg: "#fafafa",  border: "#d1d5db", dot: "#9ca3af", text: "#6b7280", label: "Not checked"    },
    checking: { bg: "#eff6ff",  border: "#93c5fd", dot: "#3b82f6", text: "#1d4ed8", label: "Checking…"      },
    ok:       { bg: "#f0fdf4",  border: "#86efac", dot: "#16a34a", text: "#15803d", label: "Ready to scan"  },
    error:    { bg: "#fef2f2",  border: "#fca5a5", dot: "#dc2626", text: "#b91c1c", label: "Not reachable"  },
    scanning: { bg: "#fffbeb",  border: "#fde68a", dot: "#f59e0b", text: "#b45309", label: "Scanning…"      },
  }[status] || { bg: "#fafafa", border: "#d1d5db", dot: "#9ca3af", text: "#6b7280", label: "Unknown" };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: cfg.bg, border: `1.5px solid ${cfg.border}`, borderRadius: 10, marginBottom: 14 }}>
      <div style={{ width: 10, height: 10, borderRadius: "50%", background: cfg.dot, flexShrink: 0,
        boxShadow: status === "ok" ? "0 0 0 3px #bbf7d0" : status === "scanning" ? "0 0 0 3px #fde68a" : "none" }} />
      <span style={{ fontWeight: 700, fontSize: 13, color: cfg.text, flex: 1 }}>
        🖨️ Epson ET-4800 ({SCANNER_IP}) — {cfg.label}
      </span>
      {status === "error" && (
        <span style={{ fontSize: 11, color: cfg.text }}>Is Epson on WiFi? Same network as server?</span>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
const fmt = (n) =>
  n !== "" && n !== null && n !== undefined
    ? "$" + Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : "—";

const defaultForm = {
  customer: "", checkNumber: "", amount: "", fee: "",
  checkDate: getTodayChicago(),
  cashedOn:  getTodayChicago(),
  maker: "", bankName: "", accountNumber: "",
};

/**
 * Main DemoCashCheck page component.
 * @component
 * @returns {React.ReactElement}
 */
export default function DemoCashCheck() {
  const navigate = useNavigate();

  const [form, setForm]                         = useState(defaultForm);
  const [customers, setCustomers]               = useState([]);
  const [searchCust, setSearchCust]             = useState("");
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showDrop, setShowDrop]                 = useState(false);
  const [companyList, setCompanyList]           = useState([]);
  const [bankList, setBankList]                 = useState([]);

  const [checkFrontPreview, setCheckFrontPreview] = useState(null);
  const [checkBackPreview,  setCheckBackPreview]  = useState(null);

  const [scanningEpson, setScanningEpson] = useState(false);
  const [scanningOCR,   setScanningOCR]   = useState(false);
  const [chequeScanned, setChequeScanned] = useState(false);
  const [scanResult,    setScanResult]    = useState(null);
  const [scannerStatus, setScannerStatus] = useState("unknown");
  const [scanLog,       setScanLog]       = useState([]);

  const frontRef = useRef();
  const backRef  = useRef();
  const logRef   = useRef();

  const set    = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const addLog = (msg, type = "info") => {
    const ts = new Date().toLocaleTimeString();
    setScanLog((p) => [...p, { ts, msg, type }]);
    console.log(`[Scanner] ${msg}`);
  };

  // scroll log to bottom
  useEffect(() => { logRef.current?.scrollIntoView({ behavior: "smooth" }); }, [scanLog]);

  // ── Load masters ───────────────────────────────────────────────────────────
  useEffect(() => {
    getBanks({ limit: 9999 }).then((r) => setBankList(r.data.data || [])).catch(() => {});
    getCompanies({ limit: 9999 }).then((r) => setCompanyList(r.data.data || [])).catch(() => {});
    handlePing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Customer search ────────────────────────────────────────────────────────
  useEffect(() => {
    if (searchCust.length >= 2)
      getCustomers({ search: searchCust, limit: 10 }).then((r) => setCustomers(r.data.data));
    else setCustomers([]);
  }, [searchCust]);

  // ── Ping scanner via server proxy ──────────────────────────────────────────
  const handlePing = async () => {
    setScannerStatus("checking");
    setScanLog([]);
    addLog("Connecting to Epson Connect...");
    try {
      const res = await checkScannerStatus(null);
      if (res.data?.reachable) {
        setScannerStatus("ok");
        addLog(`✓ Scanner ready — ${res.data.model}`, "success");
        toast.success("✅ Epson scanner connected!");
      } else {
        setScannerStatus("error");
        addLog(`✗ ${res.data?.message || "Not reachable"}`, "error");
        toast.error("Scanner not reachable.");
      }
    } catch (err) {
      setScannerStatus("error");
      addLog(`✗ ${err.message}`, "error");
    }
  };

  const selectCustomer = (c) => {
    setSelectedCustomer(c);
    set("customer", c._id);
    setShowDrop(false);
    setSearchCust("");
    if (c.isBlocked) toast.error(`⛔ ${c.firstName} ${c.lastName} is BLOCKED.`);
  };

  // ── Apply OCR data ─────────────────────────────────────────────────────────
  const applyChequeScanData = (d) => {
    const filled = [];
    if (d.maker)         { set("maker",         d.maker.trim());    filled.push(`Maker: ${d.maker}`); }
    if (d.checkNumber)   { set("checkNumber",   d.checkNumber);     filled.push(`Check #: ${d.checkNumber}`); }
    if (d.amount)        { set("amount",        d.amount);          filled.push(`Amount: $${d.amount}`); }
    if (d.date)          { set("checkDate",     d.date);            filled.push(`Date: ${d.date}`); }
    if (d.bankName)      { set("bankName",      d.bankName);        filled.push(`Bank: ${d.bankName}`); }
    if (d.accountNumber) { set("accountNumber", d.accountNumber); }
    return filled;
  };

  // ── Run OCR on image ───────────────────────────────────────────────────────
  const runOCR = async (blob, side = "front") => {
    setScanningOCR(true);
    setChequeScanned(false);
    setScanResult(null);

    const url = URL.createObjectURL(blob);
    if (side === "front") setCheckFrontPreview(url);
    else                  setCheckBackPreview(url);

    const toastId = toast.loading("🤖 AI reading cheque — auto-filling fields…");
    addLog("Sending image to AI OCR…");

    try {
      const base64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload  = () => res(r.result.split(",")[1]);
        r.onerror = rej;
        r.readAsDataURL(blob);
      });

      const res = await scanChequeImage(base64, blob.type || "image/jpeg");
      const d   = res.data?.data;
      setScanResult(d);

      if (d && Object.keys(d).some((k) => d[k])) {
        const filled = applyChequeScanData(d);
        setChequeScanned(true);
        addLog(`✓ OCR done — ${filled.length} fields filled`, "success");
        toast.success(`✅ Done! ${filled.length} fields filled.`, { id: toastId, duration: 4000 });
      } else {
        addLog("OCR found no fields — fill manually", "warn");
        toast.error("Could not read fields. Fill manually.", { id: toastId });
      }
    } catch (err) {
      addLog(`✗ OCR error: ${err.message}`, "error");
      toast.error("OCR failed. Fill manually.", { id: toastId });
    } finally {
      setScanningOCR(false);
    }
  };

  // ── 🖨️ SCAN BUTTON — calls server proxy ───────────────────────────────────
  const handleEpsonScan = async (side = "front") => {
    if (scannerStatus === "unknown") {
      toast.error("Click '🔄 Test Connection' first.");
      return;
    }
    if (scannerStatus === "error") {
      toast.error("Scanner not reachable. Check Epson WiFi connection.");
      return;
    }

    setScannerStatus("scanning");
    setScanningEpson(true);
    setScanLog([]);
    const toastId = toast.loading(`🖨️ Scanning ${side === "front" ? "Check Front" : "Check Back"}…`);
    addLog(`Starting scan — ${side} side`);

    try {
      addLog("Server connecting to Epson ET-4800…");
      const res = await doEpsonScan(null);
      const blob = res.data;
      addLog(`✓ Image received — ${(blob.size / 1024).toFixed(1)} KB`, "success");
      toast.success("✅ Scanned! Reading fields…", { id: toastId, duration: 2000 });
      setScannerStatus("ok");

      const file = new File([blob], `epson-${side}-${Date.now()}.jpg`, {
        type: "image/jpeg", lastModified: Date.now(),
      });
      await runOCR(file, side);
    } catch (err) {
      setScannerStatus("error");
      const msg = err.response?.data?.message || err.message;
      addLog(`✗ Scan failed: ${msg}`, "error");
      toast.error(`Scanner error: ${msg}`, { id: toastId, duration: 6000 });
    } finally {
      setScanningEpson(false);
    }
  };

  // ── Upload fallback ────────────────────────────────────────────────────────
  const handleFilePick = async (e, side) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (file) await runOCR(file, side);
  };

  const isBusy = scanningEpson || scanningOCR;
  const amount = parseFloat(form.amount) || 0;
  const fee    = parseFloat(form.fee)    || 0;
  const payOut = amount > 0 && form.fee !== "" ? (amount - fee).toFixed(2) : null;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Layout title="Demo Cash a Check">

      {/* Header */}
      <div className="flex items-center gap-3 mb-4" style={{ flexWrap: "wrap" }}>
        <button className="btn btn-ghost btn-sm" onClick={() => navigate("/cheques")}>← Back</button>
        <h2 style={{ fontFamily: "var(--font-display)", fontSize: 20, fontWeight: 800 }}>🖨️ Demo Cash a Check</h2>
        <span style={{ fontSize: 12, color: "white", background: "#7c3aed", padding: "3px 10px", borderRadius: 20, fontWeight: 700 }}>Epson Scanner Test</span>
      </div>

      {/* Scanner status */}
      <ScannerStatus status={scannerStatus} />

      {/* Test connection button */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
        <button
          className="btn btn-outline btn-sm"
          onClick={handlePing}
          disabled={scannerStatus === "checking"}
          style={{ fontSize: 12, fontWeight: 700 }}
        >
          {scannerStatus === "checking" ? "Checking…" : "🔄 Test Connection"}
        </button>
        <span style={{ fontSize: 12, color: "var(--gray-1)" }}>
          Scanner IP: <code style={{ fontFamily: "monospace", fontWeight: 700 }}>{SCANNER_IP}</code>
          {" — "}Make sure Epson is on WiFi and the server can reach it.
        </span>
      </div>

      {/* Scan log */}
      {scanLog.length > 0 && (
        <div style={{ background: "#0f172a", borderRadius: 10, padding: "12px 14px", marginBottom: 14, maxHeight: 130, overflowY: "auto" }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "rgba(255,255,255,0.3)", marginBottom: 6, textTransform: "uppercase", letterSpacing: 1 }}>Scanner Log</div>
          {scanLog.map((e, i) => (
            <div key={i} style={{ fontFamily: "monospace", fontSize: 11, marginBottom: 2,
              color: e.type === "error" ? "#f87171" : e.type === "success" ? "#4ade80" : e.type === "warn" ? "#fbbf24" : "rgba(255,255,255,0.65)" }}>
              <span style={{ opacity: 0.4 }}>[{e.ts}]</span> {e.msg}
            </div>
          ))}
          <div ref={logRef} />
        </div>
      )}

      {/* Main layout */}
      <div className="cashcheck-layout" style={{ display: "grid", gap: 12 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>

          {/* STEP 1 — Customer */}
          <div className="card">
            <div className="card-header" style={{ background: "linear-gradient(135deg,#0f3d2b,#1a7a4a)", borderRadius: "12px 12px 0 0" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 26, height: 26, background: "rgba(255,255,255,0.15)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color: "white" }}>1</div>
                <h3 style={{ color: "white" }}>Customer — Pay To The Order Of</h3>
              </div>
            </div>
            <div className="card-body">
              {selectedCustomer ? (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px", background: "var(--primary-light)", borderRadius: 10, border: "1.5px solid rgba(26,122,74,0.3)" }}>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 17, fontFamily: "var(--font-display)" }}>{selectedCustomer.firstName} {selectedCustomer.lastName}</div>
                    <div style={{ fontSize: 12.5, color: "var(--gray-1)", marginTop: 3 }}>{selectedCustomer.identifications?.[0]?.idNumber || "—"}</div>
                  </div>
                  <button className="btn btn-outline btn-sm" onClick={() => { setSelectedCustomer(null); set("customer", ""); }}>Change</button>
                </div>
              ) : (
                <div style={{ position: "relative" }}>
                  <input className="form-control" placeholder="🔍 Search by name, ID number, or phone…" value={searchCust}
                    onChange={(e) => { setSearchCust(e.target.value); setShowDrop(true); }} onFocus={() => setShowDrop(true)} style={{ fontSize: 14 }} />
                  {showDrop && customers.length > 0 && (
                    <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "white", border: "1.5px solid var(--gray-3)", borderRadius: 10, boxShadow: "var(--shadow-lg)", zIndex: 50, maxHeight: 260, overflowY: "auto", marginTop: 4 }}>
                      {customers.map((c) => (
                        <div key={c._id} onClick={() => selectCustomer(c)} style={{ padding: "10px 16px", cursor: "pointer", borderBottom: "1px solid var(--gray-4)" }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = "var(--primary-light)")}
                          onMouseLeave={(e) => (e.currentTarget.style.background = "white")}>
                          <div style={{ fontWeight: 700, fontSize: 14 }}>{c.firstName} {c.lastName}</div>
                          <div style={{ fontSize: 12, color: "var(--gray-1)", marginTop: 2 }}>{c.identifications?.[0]?.idNumber} · {c.contacts?.[0]?.value}</div>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ marginTop: 10, fontSize: 12.5, color: "var(--gray-1)" }}>
                    Customer not in system?{" "}
                    <button className="btn btn-ghost btn-sm" style={{ padding: "2px 8px", fontSize: 12.5 }} onClick={() => navigate("/customers/new")}>➕ Add New Customer First</button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* STEP 2 — Check Information */}
          <div className="card">
            <div className="card-header" style={{ background: "linear-gradient(135deg,#1a3a5c,#1a5c8a)", borderRadius: "12px 12px 0 0" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 26, height: 26, background: "rgba(255,255,255,0.15)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 800, color: "white" }}>2</div>
                <h3 style={{ color: "white" }}>Check Information</h3>
                <span style={{ fontSize: 11.5, color: "rgba(255,255,255,0.6)", marginLeft: 4 }}>From the physical check</span>
              </div>
            </div>
            <div className="card-body">

              {/* Status banner */}
              {(isBusy || chequeScanned) && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", borderRadius: 10, marginBottom: 14,
                  background: isBusy ? "#eff6ff" : "#ecfdf5", border: `1.5px dashed ${isBusy ? "#3b82f6" : "#10b981"}` }}>
                  {isBusy ? (
                    <>
                      <div style={{ width: 18, height: 18, border: "2.5px solid #3b82f6", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite", flexShrink: 0 }} />
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#1d4ed8" }}>
                        {scanningEpson ? "🖨️ Scanning from Epson ET-4800…" : "🤖 AI reading cheque — auto-filling fields…"}
                      </span>
                    </>
                  ) : (
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#059669" }}>✅ Scan complete! Review fields below.</span>
                  )}
                </div>
              )}

              {/* Scan tiles */}
              <div className="scan-shell">
                {/* Front */}
                <div className="scan-tile">
                  <div className={`scan-preview ${isBusy ? "is-active" : ""}`}
                    style={{ border: isBusy ? "1.5px solid #3b82f6" : "1.5px dashed var(--gray-3)", background: isBusy ? "#eff6ff" : "var(--gray-4)", cursor: isBusy ? "default" : "pointer" }}
                    onClick={() => !isBusy && handleEpsonScan("front")}>
                    {checkFrontPreview ? (
                      <img src={checkFrontPreview} alt="Front" style={{ width: "100%" }} />
                    ) : (
                      <div style={{ padding: "16px 0" }}>
                        <div style={{ fontSize: 32 }}>🖨️</div>
                        <div style={{ fontSize: 12, color: "var(--gray-1)", marginTop: 6, fontWeight: 600 }}>Check Front</div>
                        <div style={{ fontSize: 10.5, color: "var(--primary)", marginTop: 2 }}>Click Scan to auto-fill</div>
                      </div>
                    )}
                  </div>
                  {!isBusy && (
                    <div className="scan-actions">
                      <button className="btn btn-sm btn-primary"
                        style={{ fontSize: 12, fontWeight: 700, flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 5,
                          background: scannerStatus === "error" ? "#6b7280" : "var(--primary)" }}
                        onClick={() => handleEpsonScan("front")}
                        title="Place check face-down on Epson glass then click Scan">
                        🖨️ {checkFrontPreview ? "Re-Scan" : "Scan"}
                      </button>
                      <button className="btn btn-sm btn-outline"
                        style={{ fontSize: 11.5, color: "var(--gray-1)", fontWeight: 600, flex: 1 }}
                        onClick={() => frontRef.current.click()}
                        title="Upload image instead">
                        📁 Upload
                      </button>
                    </div>
                  )}
                  <input type="file" accept="image/*" ref={frontRef} onChange={(e) => handleFilePick(e, "front")} style={{ display: "none" }} />
                  <div style={{ fontSize: 10, color: "var(--gray-2)", marginTop: 4, textAlign: "center" }}>Place check face-down on scanner glass</div>
                </div>

                {/* Back */}
                <div className="scan-tile">
                  <div className="scan-preview"
                    style={{ border: "1.5px dashed var(--gray-3)", background: "var(--gray-4)", cursor: isBusy ? "default" : "pointer" }}
                    onClick={() => !isBusy && handleEpsonScan("back")}>
                    {checkBackPreview ? (
                      <img src={checkBackPreview} alt="Back" style={{ width: "100%" }} />
                    ) : (
                      <div style={{ padding: "16px 0" }}>
                        <div style={{ fontSize: 32 }}>📃</div>
                        <div style={{ fontSize: 12, color: "var(--gray-1)", marginTop: 6, fontWeight: 600 }}>Check Back</div>
                        <div style={{ fontSize: 10.5, color: "var(--gray-2)", marginTop: 2 }}>Endorsement side</div>
                      </div>
                    )}
                  </div>
                  {!isBusy && (
                    <div className="scan-actions">
                      <button className="btn btn-sm btn-outline"
                        style={{ fontSize: 12, fontWeight: 700, flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 5 }}
                        onClick={() => handleEpsonScan("back")}>
                        🖨️ {checkBackPreview ? "Re-Scan" : "Scan"}
                      </button>
                      <button className="btn btn-sm btn-outline"
                        style={{ fontSize: 11.5, color: "var(--gray-1)", flex: 1 }}
                        onClick={() => backRef.current.click()}>
                        📁 Upload
                      </button>
                    </div>
                  )}
                  <input type="file" accept="image/*" ref={backRef} onChange={(e) => handleFilePick(e, "back")} style={{ display: "none" }} />
                  <div style={{ fontSize: 10, color: "var(--gray-2)", marginTop: 4, textAlign: "center" }}>Flip check over, then scan</div>
                </div>
              </div>

              {/* Fields */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 2fr", gap: 10, marginBottom: 10 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Check Number <span style={{ color: "var(--danger)" }}>*</span></label>
                  <input className="form-control" value={form.checkNumber} onChange={(e) => set("checkNumber", e.target.value)} placeholder="3061 / 319" style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 15 }} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Date on Check <span style={{ color: "var(--danger)" }}>*</span></label>
                  <DateInput value={form.checkDate} onChange={(e) => set("checkDate", e.target.value)} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Cashed On <span style={{ color: "var(--danger)" }}>*</span></label>
                  <DateInput value={form.cashedOn} onChange={(e) => set("cashedOn", e.target.value)} maxYear={new Date().getFullYear()} />
                </div>
                <div className="form-group" style={{ margin: 0 }}>
                  <label className="form-label">Maker / Payer Name <span style={{ color: "var(--danger)" }}>*</span>
                    <span style={{ fontSize: 10, fontWeight: 500, color: "var(--primary)", marginLeft: 6 }}>(from Company Master)</span>
                  </label>
                  <MasterDropdown value={form.maker} onChange={(val) => set("maker", val)} items={companyList} placeholder="Select or type company name…" icon="🏢" />
                  <div style={{ fontSize: 11, color: "var(--gray-1)", marginTop: 3 }}>The business/person who wrote the check</div>
                </div>
              </div>

              {/* Amount */}
              <div style={{ background: "var(--gray-4)", borderRadius: 10, padding: "16px", marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--gray-1)", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 12 }}>💰 Amount Calculation</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                  <div>
                    <label className="form-label">Check Amount ($) <span style={{ color: "var(--danger)" }}>*</span></label>
                    <input className="form-control" type="number" step="0.01" min="0" value={form.amount} onChange={(e) => set("amount", e.target.value)} placeholder="130.00" style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 18 }} />
                  </div>
                  <div>
                    <label className="form-label" style={{ display: "flex", justifyContent: "space-between" }}>
                      Fee ($) <span style={{ color: "var(--danger)" }}>*</span>
                      <button type="button" style={{ fontSize: 10, color: "var(--primary)", fontWeight: 600, background: "none", border: "none", cursor: "pointer", padding: 0 }}
                        onClick={() => { if (!amount) return; set("fee", Math.max(5, amount * 0.02).toFixed(2)); }}>Auto (2%)</button>
                    </label>
                    <input className="form-control" type="number" step="0.01" min="0" value={form.fee} onChange={(e) => set("fee", e.target.value)} placeholder="8.00" style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 18, color: "var(--danger)" }} />
                  </div>
                  <div>
                    <label className="form-label">Customer Pay Out</label>
                    <div style={{ padding: "10px 14px", background: payOut ? "var(--success-light)" : "var(--gray-3)", borderRadius: "var(--radius-sm)", fontFamily: "var(--font-display)", fontWeight: 900, fontSize: 22, color: payOut ? "var(--success)" : "var(--gray-2)", textAlign: "center" }}>
                      {payOut ? fmt(payOut) : "—"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* STEP 3 — Bank */}
          <div className="card">
            <div className="card-header">
              <h3>🏦 Bank Information</h3>
              <span style={{ fontSize: 12, color: "var(--gray-1)" }}>Auto-filled from scan</span>
            </div>
            <div className="card-body">
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <label className="form-label" style={{ fontSize: 11 }}>Bank Name</label>
                  <MasterDropdown value={form.bankName || ""} onChange={(val) => set("bankName", val)} items={bankList} placeholder="Auto-filled from scan" icon="🏦" />
                </div>
                <div>
                  <label className="form-label" style={{ fontSize: 11 }}>Account Number</label>
                  <input className="form-control" value={form.accountNumber || ""} onChange={(e) => set("accountNumber", e.target.value)} placeholder="Auto-filled from scan" style={{ fontFamily: "monospace" }} />
                </div>
              </div>
            </div>
          </div>

          {/* OCR debug */}
          {scanResult && (
            <div className="card">
              <div className="card-header" style={{ background: "#1e1b4b" }}><h3 style={{ color: "white" }}>🔬 Raw OCR Response</h3></div>
              <div className="card-body" style={{ background: "#0f172a" }}>
                <pre style={{ fontFamily: "monospace", fontSize: 11.5, color: "#a5f3fc", margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                  {JSON.stringify(scanResult, null, 2)}
                </pre>
              </div>
            </div>
          )}
        </div>

        {/* RIGHT PANEL */}
        <div className="cashcheck-right-panel" style={{ position: "sticky", top: 80, display: "flex", flexDirection: "column", gap: 14 }}>
          <div className="card" style={{ background: "var(--dark)", border: "none", overflow: "hidden" }}>
            <div style={{ padding: "16px 20px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
              <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>Transaction Preview</div>
            </div>
            <div style={{ padding: "18px 20px" }}>
              <div style={{ marginBottom: 16, paddingBottom: 14, borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginBottom: 4 }}>PAY TO</div>
                <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 16, color: "white" }}>
                  {selectedCustomer ? `${selectedCustomer.firstName} ${selectedCustomer.lastName}` : "— Select Customer —"}
                </div>
              </div>
              {[{ label: "Check #", val: form.checkNumber || "—", mono: true }, { label: "Date", val: form.cashedOn || form.checkDate || "—" }, { label: "Maker", val: form.maker || "—" }].map((r) => (
                <div key={r.label} style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 13 }}>
                  <span style={{ color: "rgba(255,255,255,0.5)" }}>{r.label}</span>
                  <span style={{ color: "white", fontFamily: r.mono ? "monospace" : undefined, fontWeight: r.mono ? 700 : undefined }}>{r.val}</span>
                </div>
              ))}
              <div style={{ height: 1, background: "rgba(255,255,255,0.08)", margin: "14px 0" }} />
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8, fontSize: 13 }}>
                <span style={{ color: "rgba(255,255,255,0.5)" }}>Check Amount</span>
                <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, color: "white", fontSize: 16 }}>{form.amount ? fmt(form.amount) : "—"}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14, fontSize: 13 }}>
                <span style={{ color: "rgba(255,255,255,0.5)" }}>Fee</span>
                <span style={{ fontFamily: "var(--font-display)", fontWeight: 700, color: "var(--danger)", fontSize: 14 }}>{form.fee !== "" ? `- ${fmt(form.fee)}` : "—"}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", background: payOut ? "rgba(16,185,129,0.15)" : "rgba(255,255,255,0.05)", borderRadius: 10, border: `1.5px solid ${payOut ? "rgba(16,185,129,0.4)" : "rgba(255,255,255,0.08)"}` }}>
                <span style={{ fontSize: 13, color: "rgba(255,255,255,0.7)", fontWeight: 700 }}>CUSTOMER PAYOUT</span>
                <span style={{ fontFamily: "var(--font-display)", fontWeight: 900, fontSize: 26, color: payOut ? "#10b981" : "rgba(255,255,255,0.2)" }}>{payOut ? fmt(payOut) : "—"}</span>
              </div>
              {(checkFrontPreview || checkBackPreview) && (
                <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
                  {checkFrontPreview && <img src={checkFrontPreview} alt="Front" style={{ flex: 1, height: 50, objectFit: "cover", borderRadius: 6, border: "1px solid rgba(255,255,255,0.1)" }} />}
                  {checkBackPreview  && <img src={checkBackPreview}  alt="Back"  style={{ flex: 1, height: 50, objectFit: "cover", borderRadius: 6, border: "1px solid rgba(255,255,255,0.1)" }} />}
                </div>
              )}
            </div>
          </div>
          <div style={{ padding: "14px 18px", background: "#7c3aed", borderRadius: 12, color: "white", textAlign: "center" }}>
            <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 4 }}>🧪 Demo Mode</div>
            <div style={{ fontSize: 12, opacity: 0.85 }}>Does not submit transactions. Tests Epson scanner + AI OCR only.</div>
          </div>
          <div style={{ padding: "14px", background: "var(--accent-light)", borderRadius: 10, border: "1px solid rgba(240,165,0,0.2)" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#92400e", marginBottom: 8 }}>📋 CASHIER CHECKLIST</div>
            {["Verify customer ID matches check name", "Check ID is not expired", "Verify check signature on back", "Confirm MICR routing/account numbers", "Photograph front and back of check"].map((tip, i) => (
              <div key={i} style={{ display: "flex", gap: 8, fontSize: 11.5, color: "#78350f", marginBottom: 4 }}><span>□</span><span>{tip}</span></div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}
